import { initContract } from "@ts-rest/core";
import { z } from "zod";
/**
 * The task board at `/task-board` (`.claude/plans/task-board.md` Part B) — the wire shapes shared by the API, the page
 * and Claude's MCP tools. One board per realm, SuperAdmin only. The page reads; the MCP tools write.
 *
 * A task's text is Markdown. `version` counts changes of the title or text and is the lock for those edits; a state
 * change or a section move leaves it alone. A finished task is deleted by `closeTask`, which keeps its name and the date.
 */
/** The display order is the order here. */
export const TaskBoardSectionSchema = z.enum(["cross", "web", "web-frontend", "vr", "mobile", "hololens", "konzepte"]);
export const TaskBoardStateSchema = z.enum(["open", "started"]);
const Slug = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,199}$/);
const Title = z.string().trim().min(1).max(500);
const Content = z.string().max(90000);
const Person = z.string().trim().min(1).max(200);
const Version = z.number().int().min(1).max(2147483647);
const IdParam = z.coerce.number().int().positive().max(2147483647);
export const TaskBoardTaskSchema = z.object({
    id: z.number().int(),
    section: TaskBoardSectionSchema,
    slug: z.string(),
    title: z.string(),
    version: z.number().int(),
    state: TaskBoardStateSchema,
    assignee: z.string().nullable(),
    branch: z.string().nullable(),
    updatedAt: z.string(),
});
export const TaskBoardTaskDetailSchema = TaskBoardTaskSchema.extend({ contentMd: z.string() });
export const TaskBoardClosedSchema = z.object({ id: z.number().int(), name: z.string(), closedAt: z.string() });
/** `open` carries nobody; `started` always carries the person and the branch. */
const StateFields = z.discriminatedUnion("state", [
    z.strictObject({ state: z.literal("open") }),
    z.strictObject({ state: z.literal("started"), assignee: Person, branch: Person, force: z.boolean().optional() }),
]);
export const TaskBoardCreateSchema = z.strictObject({
    section: TaskBoardSectionSchema,
    slug: Slug,
    title: Title,
    contentMd: Content,
    /** Only for a restore from an export: the stored number and state instead of a fresh task. */
    restore: z
        .strictObject({ version: Version, started: z.strictObject({ assignee: Person, branch: Person }).nullable() })
        .optional(),
});
export const TaskBoardUpdateSchema = z
    .strictObject({ title: Title.optional(), contentMd: Content.optional(), section: TaskBoardSectionSchema.optional(), ifVersion: Version })
    .refine((b) => b.title !== undefined || b.contentMd !== undefined || b.section !== undefined, { message: "nothing to change" });
const c = initContract();
const NotFound = z.object({ message: z.string(), code: z.literal("TASK_BOARD_NOT_FOUND") });
const Forbidden = z.object({ message: z.string(), code: z.literal("TASK_BOARD_FORBIDDEN") });
const Conflict = z.object({ message: z.string(), code: z.literal("TASK_BOARD_CONFLICT") });
/** The task is already started by someone else, or on another branch. */
const Taken = z.object({ message: z.string(), code: z.literal("TASK_ALREADY_TAKEN"), assignee: z.string().nullable(), branch: z.string().nullable() });
const Message = z.object({ message: z.string() });
/** Every route answers 403 `TASK_BOARD_FORBIDDEN` for anyone but a SuperAdmin of the realm. */
export const taskBoardContract = c.router({
    listTaskBoardTasks: {
        method: "GET",
        path: "/task-board",
        query: z.object({ section: TaskBoardSectionSchema.optional(), state: TaskBoardStateSchema.optional(), slug: Slug.optional() }),
        responses: { 200: z.object({ tasks: z.array(TaskBoardTaskSchema) }), 400: Message, 401: Message, 403: Forbidden },
        summary: "The realm's tasks and concepts, without their text.",
    },
    getTaskBoardTask: {
        method: "GET",
        path: "/task-board/tasks/:id",
        pathParams: z.object({ id: IdParam }),
        responses: { 200: TaskBoardTaskDetailSchema, 401: Message, 403: Forbidden, 404: NotFound },
        summary: "One task with its Markdown.",
    },
    createTaskBoardTask: {
        method: "POST",
        path: "/task-board/tasks",
        body: TaskBoardCreateSchema,
        responses: { 201: TaskBoardTaskSchema, 400: Message, 401: Message, 403: Forbidden, 409: Conflict },
        summary: "Create a task. 409 when the section already has this slug, ignoring case.",
    },
    updateTaskBoardTask: {
        method: "PATCH",
        path: "/task-board/tasks/:id",
        pathParams: z.object({ id: IdParam }),
        body: TaskBoardUpdateSchema,
        responses: { 200: TaskBoardTaskSchema, 400: Message, 401: Message, 403: Forbidden, 404: NotFound, 409: Conflict },
        summary: "Change the title, text or section, against `ifVersion`. Only a title or text change raises the version.",
    },
    setTaskBoardTaskState: {
        method: "PUT",
        path: "/task-board/tasks/:id/state",
        pathParams: z.object({ id: IdParam }),
        body: StateFields,
        responses: { 200: TaskBoardTaskSchema, 400: Message, 401: Message, 403: Forbidden, 404: NotFound, 409: Taken },
        summary: "Start a task or put it back to open. 409 when someone else holds it, unless `force`. 400 for a concept.",
    },
    closeTaskBoardTask: {
        method: "POST",
        path: "/task-board/tasks/:id/close",
        pathParams: z.object({ id: IdParam }),
        body: z.strictObject({ ifVersion: Version }),
        responses: { 200: TaskBoardClosedSchema, 400: Message, 401: Message, 403: Forbidden, 404: NotFound, 409: Conflict },
        summary: "Finish a task: deletes it and keeps its name and the date. Cannot be undone. 400 for a concept.",
    },
    deleteTaskBoardTask: {
        method: "DELETE",
        path: "/task-board/tasks/:id",
        pathParams: z.object({ id: IdParam }),
        query: z.object({ ifVersion: z.coerce.number().int().min(1).max(2147483647) }),
        body: z.object({}).optional(),
        responses: { 200: z.object({ deleted: z.literal(true) }), 400: Message, 401: Message, 403: Forbidden, 404: NotFound, 409: Conflict },
        summary: "Delete a task created by mistake. Leaves no history row.",
    },
    listTaskBoardClosed: {
        method: "GET",
        path: "/task-board/closed",
        query: z.object({ limit: z.coerce.number().int().min(1).max(500).default(100), offset: z.coerce.number().int().min(0).default(0) }),
        responses: { 200: z.object({ closed: z.array(TaskBoardClosedSchema), total: z.number().int() }), 400: Message, 401: Message, 403: Forbidden },
        summary: "The history of finished tasks, newest first.",
    },
    addTaskBoardClosed: {
        method: "POST",
        path: "/task-board/closed",
        body: z.strictObject({ name: Title, closedAt: z.string().datetime() }),
        responses: { 201: TaskBoardClosedSchema, 400: Message, 401: Message, 403: Forbidden },
        summary: "Add a history row with a given date, for an import or a restore.",
    },
});
//# sourceMappingURL=task-board.contract.js.map